import React from 'react';
import { Controller } from 'react-hook-form';
import { Pressable } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CountryRegionField, StateProvinceField } from '@/components/address-fields';
import { Stack } from '@/components/layout';
import {
  Button,
  Checkbox,
  ControlledInput,
  ControlledSelectInput,
  DateTimePicker,
  SvgIcon,
  Text,
} from '@/components/ui';
import type { FormInputProps } from '@/components/ui/form/form-input';
import type { FormSelectInputProps } from '@/components/ui/form/form-select-input';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import { useAddPlayer } from '@/features/guardian/hooks/use-add-player';
import { GENDER_OPTIONS } from '@/features/guardian/schemas/guardian-add-player-schema';
import { OnboardingAvatar } from '@/features/onboarding/components/onboarding-avatar';
import {
  DOMINANT_HAND_OPTIONS,
  HEIGHT_OPTIONS,
} from '@/features/onboarding/schemas/add-player-schema';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { MAX_CITY_LENGTH, MAX_NAME_LENGTH, MAX_ZIP_LENGTH } from '@/validation';

const strings = STRINGS.onboarding.addPlayer;

/**
 * PP-376 — full-screen "Add / Edit Player" reachable from the Guardian Homepage (create) and
 * the Player Detail header (edit). All form/mutation logic lives in `useAddPlayer`; this file
 * is the view only.
 */
export const AddPlayerScreen = () => {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const insets = useSafeAreaInsets();

  const {
    control,
    isEdit,
    screenTitle,
    sameAddress,
    country,
    onBack,
    onPickAvatar,
    onToggleSameAddress,
    onChangeCountry,
    onDelete,
    onSubmit,
  } = useAddPlayer();

  // Match the onboarding add-player form look (8px radius, 48px height, colours) so the
  // fields render exactly like the design instead of the default FormInput/Select styling.
  const sharedFieldProps = {
    uppercaseLabel: false,
    labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
    inputVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    labelGap: scale(4),
    labelStyle: styles.fieldLabel,
    inputStyle: styles.fieldText,
    contentStyle: styles.fieldContent,
  } satisfies Partial<FormInputProps>;

  const selectFieldProps = {
    uppercaseLabel: false,
    labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
    labelGap: scale(4),
    triggerVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    labelStyle: styles.fieldLabel,
    triggerStyle: styles.fieldContent,
  } satisfies Partial<FormSelectInputProps>;

  return (
    <Stack flex={1} backgroundColor={colors['1']}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack}
        titleAlign="center"
        // Edit: header right action — Delete (trash) on tablet, Save on phone.
        {...(isEdit
          ? isTablet
            ? {
                rightIcon: 'IcDelete' as const,
                rightLabel: strings.deleteConfirm,
                rightColor: guardianColors.Error,
                rightAccessibilityLabel: strings.deleteConfirm,
                onPressRight: onDelete,
              }
            : {
                rightLabel: strings.saveButton,
                rightColor: guardianColors.Blue,
                rightAccessibilityLabel: strings.saveButton,
                onPressRight: onSubmit,
              }
          : {})}
      />

      <KeyboardAwareScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}
        extraKeyboardSpace={scale(12)}>
        <Stack width="100%" maxWidth={isTablet ? scale(380) : undefined} alignSelf="center">
          {/* Avatar */}
          <Stack align="center" marginBottom={scale(24)}>
            <Controller
              name="avatar"
              control={control}
              render={({ field: { value }, fieldState: { error } }) => (
                <OnboardingAvatar
                  source={value?.uri}
                  onPress={onPickAvatar}
                  hasError={Boolean(error)}
                  errorMessage={error?.message}
                />
              )}
            />
          </Stack>

          {/* Name row */}
          <Stack direction="row" gap={scale(12)}>
            <Stack flex={1}>
              <ControlledInput
                {...sharedFieldProps}
                control={control}
                name="firstName"
                label={strings.labelFirstName}
                required
                placeholder={strings.placeholderFirstName}
                autoCapitalize="words"
                maxLength={MAX_NAME_LENGTH}
              />
            </Stack>
            <Stack flex={1}>
              <ControlledInput
                {...sharedFieldProps}
                control={control}
                name="lastName"
                label={strings.labelLastName}
                required
                placeholder={strings.placeholderLastName}
                autoCapitalize="words"
                maxLength={MAX_NAME_LENGTH}
              />
            </Stack>
          </Stack>

          {/* Birth date + gender */}
          <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
            <Stack flex={1} gap={scale(4)}>
              <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.label}>
                {strings.labelBirthDate}
                <Text style={styles.required}> *</Text>
              </Text>
              <Controller
                name="dob"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <DateTimePicker
                      value={value}
                      onDateChange={onChange}
                      placeholder={strings.placeholderBirthDate}
                      maximumDate={new Date()}
                      status={error?.message ? 'error' : 'default'}
                      triggerStyle={styles.fieldContent}
                      iconName="IcCalendar2"
                      iconColor={guardianColors[6]}
                      iconSize={scale(20)}
                      displayVariant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                      presentation="sheet"
                      title={strings.birthDateSheetTitle}
                    />
                    {error?.message ? (
                      <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.fieldError}>
                        {error.message}
                      </Text>
                    ) : null}
                  </>
                )}
              />
            </Stack>
            <Stack flex={1}>
              <ControlledSelectInput
                {...selectFieldProps}
                control={control}
                name="gender"
                presentation="sheet"
                label={strings.labelGender}
                required
                options={GENDER_OPTIONS}
                placeholder={isEdit ? '--' : strings.genderPlaceholder}
              />
            </Stack>
          </Stack>

          {/* Height + dominant hand — edit mode only. */}
          {isEdit ? (
            <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
              <Stack flex={1}>
                <ControlledSelectInput
                  {...selectFieldProps}
                  control={control}
                  name="height"
                  presentation="sheet"
                  label={strings.labelHeight}
                  required={false}
                  options={HEIGHT_OPTIONS}
                  placeholder={strings.placeholderHeight}
                />
              </Stack>
              <Stack flex={1}>
                <ControlledSelectInput
                  {...selectFieldProps}
                  control={control}
                  name="dominantHand"
                  presentation="sheet"
                  label={strings.labelDominantHand}
                  required={false}
                  options={DOMINANT_HAND_OPTIONS}
                />
              </Stack>
            </Stack>
          ) : null}

          {/* Address */}
          <Stack marginTop={scale(20)} marginBottom={scale(isEdit ? 4 : 16)}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
              style={styles.sectionHeader}>
              {strings.sectionAddress}
            </Text>
          </Stack>

          {/* "Same address" is a create-only shortcut; editing shows the player's own address. */}
          {isEdit ? null : (
            <Controller
              name="sameAddress"
              control={control}
              render={({ field: { value } }) => (
                <Checkbox checked={value} onValueChange={onToggleSameAddress}>
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                    style={styles.checkboxLabel}>
                    {strings.sameAddress}
                  </Text>
                </Checkbox>
              )}
            />
          )}

          {/* Address fields are hidden when reusing the guardian's address (BR-05). */}
          {sameAddress ? null : (
            <>
              {/* Country + State/Province */}
              <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
                <Stack flex={1}>
                  <CountryRegionField
                    {...selectFieldProps}
                    showDialCode
                    label={strings.labelCountry}
                    required={false}
                    value={country}
                    onValueChange={onChangeCountry}
                    placeholder={strings.placeholderCountry}
                  />
                </Stack>
                <Stack flex={1}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <StateProvinceField
                        {...sharedFieldProps}
                        {...selectFieldProps}
                        country={country}
                        label={strings.labelStateProvince}
                        required={false}
                        value={value}
                        onValueChange={onChange}
                        status={error?.message ? 'error' : 'default'}
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Stack>
              </Stack>

              {/* City + Zip */}
              <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
                <Stack flex={1}>
                  <ControlledInput
                    {...sharedFieldProps}
                    control={control}
                    name="city"
                    label={strings.labelCity}
                    required={false}
                    placeholder={strings.placeholderCity}
                    autoCapitalize="words"
                    maxLength={MAX_CITY_LENGTH}
                  />
                </Stack>
                <Stack flex={1}>
                  <ControlledInput
                    {...sharedFieldProps}
                    control={control}
                    name="zipCode"
                    label={strings.labelZip}
                    required={false}
                    sanitize={zip => zip.replace(/[^0-9]/g, '')}
                    placeholder={strings.placeholderZip}
                    keyboardType="numeric"
                    maxLength={MAX_ZIP_LENGTH}
                  />
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      </KeyboardAwareScrollView>

      <Stack
        paddingX={scale(18)}
        paddingTop={scale(12)}
        style={{ paddingBottom: insets.bottom + scale(12) }}>
        <Stack width="100%" maxWidth={isTablet ? scale(380) : undefined} alignSelf="center">
          {!isTablet && isEdit ? (
            // Phone edit: Save lives in the header; the footer is the Delete action.
            <Pressable
              onPress={onDelete}
              hitSlop={8}
              accessibilityRole="button"
              style={styles.deleteButton}>
              <SvgIcon name="IcXClose" size={scale(20)} color={guardianColors.Error} />
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.deleteLabel}>
                {strings.deletePlayer}
              </Text>
            </Pressable>
          ) : (
            // Tablet (create + edit) and phone create: a full-width Save button.
            <Button onPress={onSubmit} style={styles.saveButton}>
              <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
                {strings.saveButton}
              </Button.Text>
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const useStyles = createStyles(({ scale }) => ({
  scroll: {
    flex: 1,
  },
  content: {
    paddingHorizontal: scale(18),
    paddingTop: scale(16),
    paddingBottom: scale(20),
  },
  label: {
    color: colors['10'],
  },
  required: {
    color: colors.Red,
  },
  fieldLabel: {
    color: colors['10'],
  },
  fieldText: {
    color: colors['10'],
    marginLeft: 0,
  },
  fieldContent: {
    borderColor: colors['3'],
    borderRadius: scale(8),
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
    color: colors['10'],
  },
  saveButton: {
    height: scale(48),
    borderRadius: scale(12),
  },
  headerSave: {
    color: guardianColors['Main/Main'],
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
  },
  deleteLabel: {
    color: guardianColors.Error,
  },
  sectionHeader: {
    color: colors['10'],
  },
  checkboxLabel: {
    color: colors['10'],
  },
  fieldError: {
    marginTop: scale(2),
    color: colors.Error,
  },
}));

export default AddPlayerScreen;
